<template>
    <div>
        <div class="packlist-actions">
            <div class="actions-container">
                <!-- Left Side: Small Icon Buttons -->
                <div class="icon-buttons-container">
                    <v-btn
                        color="primary"
                        icon
                        @click="handleFullscreen"
                    >
                        <v-icon>mdi-fullscreen</v-icon>
                    </v-btn>
                    <v-btn
                        color="primary"
                        icon
                        @click="openSettingsModal"
                    >
                        <v-icon>mdi-cog</v-icon>
                    </v-btn>
                </div>
                
                <!-- Right Side: Main Action Buttons -->
                <div class="main-buttons-group">
                    <!-- Package Size Selector - Inline with buttons -->
                    <div v-if="workflow.needsPackageSize" class="inline-package-selector">
                        <span class="selector-label">Paketgröße:</span>
                        <div class="selector-buttons">
                            <v-btn
                                @click="handleSizeChange(true)"
                                :color="under8cm === true ? 'primary' : ''"
                                :outlined="under8cm !== true"
                                small
                                class="size-btn">
                                <v-icon x-small left>mdi-package-variant-closed</v-icon>
                                Unter 8cm
                            </v-btn>
                            <v-btn
                                @click="handleSizeChange(false)"
                                :color="under8cm === false ? 'primary' : ''"
                                :outlined="under8cm !== false"
                                small
                                class="size-btn">
                                <v-icon x-small left>mdi-package-variant</v-icon>
                                Über 8cm
                            </v-btn>
                        </div>
                    </div>

                    <v-btn
                        class="action-button"
                        color="warning"
                        large
                        @click="openMulticartModal"
                        v-if="orderIsMulticart"
                    >
                        <v-icon>mdi-receipt</v-icon>
                        Warenkorbregal
                    </v-btn>
                    <v-btn
                        v-if="!labelPrinted && !hasMissingItems && workflow.canPrint"
                        class="action-button"
                        color="primary"
                        large
                        :loading="isPrinting"
                        :disabled="isPrinting"
                        @click="printLabel"
                    >
                        <template v-slot:loader>
                            <v-progress-circular
                                indeterminate
                                color="white"
                                size="24"
                            ></v-progress-circular>
                        </template>
                        <v-icon v-if="!isPrinting">mdi-printer</v-icon>
                        <span class="ml-1">{{ isPrinting ? 'Druckt...' : 'Label drucken' }}</span>
                    </v-btn>
                    <v-btn
                        v-if="labelPrinted && !isPrinting"
                        class="action-button"
                        color="success"
                        large
                        @click="pack"
                    >
                        <v-icon>mdi-package-variant</v-icon>
                        Packen
                    </v-btn>
                </div>
            </div>
        </div>

        <div class="packlist-info">
            <div v-if="currentPrinter" class="current-printer-display">
                <span class="printer-label">Drucker:</span>
                <span class="printer-name">{{ currentPrinter.name }}</span>
                <v-chip 
                    :color="currentPrinter.online ? 'success' : 'error'" 
                    x-small 
                    text-color="white"
                >
                    <v-icon left x-small>
                        {{ currentPrinter.online ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                    {{ currentPrinter.online ? 'Online' : 'Offline' }}
                </v-chip>
            </div>
            <span>-</span>
            <div class="packlist-name">
                <span class="packlist-name-label">Packliste:</span>
                <span class="packlist-name-value">{{ currentPacklistName }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import PackageSizeSelector from './PackageSizeSelector.vue';

export default {
    name: 'PacklistActions',
    components: {
        PackageSizeSelector
    },
    props: {
        hasMissingItems: {
            type: Boolean,
            default: false
        },
        orderIsMulticart: {
            type: Boolean,
            default: false
        },
        currentPrinter: {
            type: Object,
            default: null
        },
        currentPacklistName: {
            type: String,
            default: null
        },
        showPackageSizeSelector: {
            type: Boolean,
            default: true
        },
        currentOrder: {
            type: Object,
            default: null
        },
        labelPrinted: {
            type: Boolean,
            default: false
        },
        isPrinting: {
            type: Boolean,
            default: false
        },
        workflow: {
            type: Object,
            default: () => ({
                needsPackageSize: false,
                canPrint: false,
                canPack: false
            })
        }
    },
    data() {
        return {
            under8cm: null  // null = unselected, true = under 8cm, false = over 8cm
        }
    },
    methods: {
        openMulticartModal() {
            this.$emit('open-multicart-modal');
        },
        openSettingsModal() {
            this.$emit('open-settings-modal');
        },
        printLabel() {
            this.$emit('print-label', this.under8cm);
        },
        handleFullscreen() {
            this.$emit('handle-fullscreen');
        },
        handleSizeChange(value) {
            this.under8cm = value;
            this.$emit('size-changed', value);
        },
        pack() {
            this.$emit('pack');
        }
    }
}
</script>

<style>
.packlist-actions {
    display: flex;
    justify-content: center;
    padding: 16px;
    background-color: #f0f0f0;
}

.actions-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.main-buttons-group {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: nowrap;
    justify-content: flex-end;
}

.icon-buttons-container {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.action-button {
    white-space: nowrap;
    min-height: 48px;
    min-width: 140px;
    flex: 0 1 auto;
}

.packlist-info {
    padding: 8px 16px;
    background-color: #f8f9fa;
    border-top: 1px solid #e0e0e0;
    font-size: 12px;
    color: #666;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
}

.current-printer-display,
.packlist-name {
    display: flex;
    align-items: center;
    gap: 4px;
}

.printer-label,
.packlist-name-label {
    font-weight: 500;
    margin-right: 4px;
}

.printer-name,
.packlist-name-value {
    font-weight: 600;
    color: #333;
}

/* Inline Package Selector */
.inline-package-selector {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    background-color: #fff3e0;
    border: 2px solid #ff9800;
    border-radius: 8px;
    flex-shrink: 0;
}

.selector-label {
    font-size: 14px;
    font-weight: 600;
    color: #e65100;
    white-space: nowrap;
}

.selector-buttons {
    display: flex;
    gap: 8px;
}

.size-btn {
    font-size: 12px !important;
    font-weight: 500 !important;
    min-width: 90px !important;
}

/* Responsive adjustments for button spacing */
@media (max-width: 600px) {
    .main-buttons-group {
        gap: 8px;
    }

    .action-button {
        min-width: 120px;
        font-size: 0.875rem;
    }

    .inline-package-selector {
        flex-direction: column;
        gap: 8px;
        padding: 8px;
    }
}

@media (max-width: 400px) {
    .main-buttons-group {
        flex-direction: column;
        gap: 8px;
        width: 100%;
    }

    .action-button {
        width: 100%;
        min-width: auto;
    }

    .inline-package-selector {
        width: 100%;
    }

    .selector-buttons {
        width: 100%;
        justify-content: center;
    }
}

</style>